import { captureMessage } from '@sentry/node';
import get from 'lodash/get';
import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

import getCampaigns from '../../api/Campaigns';
import { createOrder, checkOrder } from '../../api/wave/orders';
import CampaignStore from '../../store/campaign';
import Cart, {
  cartPrice,
  productsForOrder,
  productsQuantities,
  ValuesToBeUpdated,
  totalCartPriceStore,
  createFromCart,
} from '../../store/cart';
import DeliveryMode from '../../store/deliveryMode';
import GuestUserStore from '../../store/guestUser';
import Loader from '../../store/loader';
import { CouponDiscount, Order } from '../../store/order';
import ProductsCollection from '../../store/productsCollection';
import { Shop, TakeAwayShops } from '../../store/shop';
import User from '../../store/user';
import { getProductsCartInfo, purchase } from '../../utils/analytics';
import { getOffersWithPriceChange } from '../../utils/getOffers';
import AppRatingModal from '../AppRatingModal/AppRatingModal';
import { PrimaryButton } from '../Buttons/Buttons';
import ConfirmationModal from '../ConfirmationModal';
import TakeAwayModal from '../GenericModal/TakeAwayStores/TakeAwayModal';
import InfoModal from '../InfoModal';
import OrderAlertSuccess from '../OrderAlertModal/OrderAlertSuccess';
import ProductsChangesModal from '../ProductChangesModal/ProductChangesModal';

import AddressPicker from './AddressPicker';
import style from './OrderDeliveryForm.module.scss';
import OrderPreferences from './OrderPreferences';
import ShopPicker from './ShopPicker';
import TimeSlotsToDeliver from './TimeSlotsToDeliver';

const INITIAL_DELIVERY_MODE = '';
const DEFAULT_NO_TIMESLOT_SELLECTED_MESSAGE = 'Παρακαλώ επιλέξτε ώρα παράδοσης';
const DEFAULT_ERROR_MESSAGE = 'Κάτι πήγε στραβά. Η παραγγελία σας δεν ολοκληρώθηκε. Παρακαλώ προσπαθήστε ξανά.';
const DELIVERY_UNSUPPORTED_POSTAL_CODE_ERROR =
  'Η περιοχή σου δεν εξυπηρετείται προς το παρόν. Μπορείς να περιηγηθείς στην εφαρμογή και να επιλέξεις άλλη διεύθυνση παράδοσης.';
const TAKEAWAY_UNSUPPORTED_POSTAL_CODE_ERROR =
  'Προς το παρόν δεν υπάρχουν διαθέσιμες ημέρες ή ώρες παράδοσης για παραγγελίες. Παρακαλούμε δοκιμάστε αργότερα.';

export default function OrderDeliveryForm() {
  const [deliveryMode, setDeliveryMode] = useState(INITIAL_DELIVERY_MODE);
  const [takeAwayShops, setTakeAwayShops] = useState([]);
  const [postalCodeUnsupported, setPostalCodeUnsupported] = useState(false);
  const [bagsInfo, setBagsInfo] = useState({ amount: 0, price: 0 });
  const [isEligibleToRate, setIsEligibleToRate] = useState(false);
  const [isModalSuccessOpen, setIsModalSuccessOpen] = useState(false);
  const [isTakeAwayModalOpen, setOpenTakeAwayModal] = useState(false);
  const [isAppRatingModalOpen, setIsAppRatingModalOpen] = useState(false);
  const [guestUser, setGuestUser] = useState({});

  const [modalType, setModalType] = useState('');
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [isProductChanges, setIsProductChanges] = useState(false);
  const [unavailableProducts, setUnavailableProducts] = useState([]);
  const [priceChangeProducts, setPriceChangeProducts] = useState([]);
  const [finalCartPrice, setFinalCartPrice] = useState(0);
  const [fee, setFee] = useState(0);

  const router = useRouter();
  let orderCheckResponse;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
    defaultValues: {
      couponNo: '',
      AFM: '',
      DOY: '',
      companyAddress: '',
      companyName: '',
      companyPhone: '',
      profession: '',
    },
  });

  const couponNo = useWatch({
    control,
    name: 'couponNo',
  });

  useEffect(() => {
    if (GuestUserStore.getValue() || User.getValue()) {
      if (createFromCart.getValue()) {
        createFromCart.update(false);
      } else {
        router.push('/cart', '/cart');
      }
    } else {
      router.push('/');
    }
  }, []);

  function onClickSelectStore(store) {
    setOpenTakeAwayModal(false);
    Order.deleteProperty(['timeSlotEnd', 'timeSlotStart', 'timeslotId']);
    Order.setShopId(store._id);
    const prevService = DeliveryMode.getValue();
    const prevShop = Shop.getValue();
    ValuesToBeUpdated.updateService(prevService);
    ValuesToBeUpdated.updateShop(prevShop?.takeAwayShop);
    DeliveryMode.update('takeAway');
    Shop.setTakeAwayShop(store);
  }

  const EXPRESS_FEE = Number(process.env.expressFee);
  const PENALTY_FEE = Number(process.env.deliveryFee);
  const MIN_CART_TAKEAWAY = Number(process.env.minCartTakeAway);

  const MIN_CART_PRICE = Number(process.env.minCartPrice) || 4000;
  const alertTextExpress = `To κόστος της υπηρεσίας είναι ${String(
    (EXPRESS_FEE / 100).toFixed(2)
  )}€  και θα προστεθεί στo συνολικό ποσό της παραγγελίας σας.`;
  const alertTextEasy = `Για παραγγελίες κάτω των ${String(
    (MIN_CART_PRICE / 100).toFixed(2)
  )}€, υπάρχει επιβάρυνση μεταφορικών ${String((PENALTY_FEE / 100).toFixed(2))}€. Θέλετε να συνεχίσετε;`;

  function handleCreateOrderError() {
    const userMessageServer =
      'Η σύνδεσή σας στο ίντερνετ αντιμετωπίζει κάποιο πρόβλημα. Παρακαλούμε προσπαθήστε αργότερα.';
    setErrorModalMessage(userMessageServer);
    setModalType('genericErrorInfo');
    CampaignStore.update([]);
  }

  function onClickOkReceipt() {
    if (!isEligibleToRate || guestUser?.email) {
      return router.push('/');
    }
    setIsModalSuccessOpen(false);
    setIsAppRatingModalOpen(true);
  }

  function onSuccessfulRatingSubmit() {
    setTimeout(() => {
      router.push('/');
    }, 2500);
  }

  function onClickSubmit() {
    if (!Order.getTimeSlotStart()) {
      setModalType('noTimeslot');
      setErrorModalMessage(DEFAULT_NO_TIMESLOT_SELLECTED_MESSAGE);
      return true;
    }
    return false;
  }

  async function onSubmitOrder() {
    const price = cartPrice.getValue();
    console.debug('OrderDeliveryForm', 'onSubmitOrder', 'price:', price, 'deliveryMode', deliveryMode);

    if (deliveryMode === 'express') {
      setErrorModalMessage(alertTextExpress);
      setIsConfirmationModalOpen(true);
      return;
    }
    if (deliveryMode === 'easy' && price < MIN_CART_PRICE) {
      setErrorModalMessage(alertTextEasy);
      setIsConfirmationModalOpen(true);
      return;
    }
    if (deliveryMode === 'takeAway' && price < MIN_CART_TAKEAWAY) {
      onAcceptCharges();
      return;
    }
    onAcceptCharges();
  }

  async function onAcceptCharges() {
    Loader.addEvent('SUBMIT_ORDER');
    try {
      setIsConfirmationModalOpen(false);

      const currentOrder = Order.getValue();

      const order = {
        ...currentOrder,
        user: {
          phone: guestUser?.contactPhone,
          email: guestUser?.email,
          isGuestUser: Boolean(guestUser?.email),
        },
      };

      const response = await createOrder(order);

      if (response.error) {
        console.debug('OrderDeliveryForm', 'onAcceptCharges', 'response.error', JSON.stringify(response.error));
        const userMessage = get(response, 'error.response.data.error.userMessage', DEFAULT_ERROR_MESSAGE);
        const errorCode = response?.error?.response?.data?.error?.code;
        if (errorCode === 'ORDVD032') {
          const userMessageError = response?.error?.response?.data?.error?.userMessage;
          const disabledDiscountCampaignIds =
            response?.error?.response?.data?.error?.params?.disabledDiscountCampaignIds;
          setErrorModalMessage(userMessageError);
          setModalType('genericErrorInfo');
          const { campaigns, error } = await getCampaigns(true);
          if (error) {
            handleCreateOrderError();
            return;
          }
          const service = DeliveryMode.getValue();
          const collectionType = DeliveryMode.getCollectionAsNumber(service);
          await ProductsCollection.updateCollection(collectionType);
          const discountCampaignsApplied = order?.discountCampaignsApplied.filter(
            campaignID => !disabledDiscountCampaignIds.includes(campaignID)
          );
          Order.setDiscountCampaignsApplied(discountCampaignsApplied);
          CampaignStore.update(campaigns);
          return;
        }
        if (errorCode === 'ORDINT203') {
          captureMessage(
            // eslint-disable-next-line max-len
            `Price missmatch:  finalCartPrice  ${finalCartPrice} bags Price  ${bagsInfo.price} fee  ${fee} deliveryMode  ${deliveryMode}`
          );
          const correctTotalPrice = response?.error?.response?.data?.error?.params?.finalPrice;
          Order.setTotalPrice(correctTotalPrice);
          const correctTotalPriceFormatted = (correctTotalPrice / 100).toFixed(2);
          setErrorModalMessage(`${userMessage} ${correctTotalPriceFormatted} €`);
          setIsConfirmationModalOpen(true);
          return;
        }
        setErrorModalMessage(userMessage);
        setModalType('genericErrorInfo');
        return;
      }

      const { order: orderCreated } = response;
      const user = User.getValue();
      console.debug('OrderDeliveryForm', 'onAcceptCharges', 'orderCreated', orderCreated);

      purchase(
        getProductsCartInfo(currentOrder.products),
        Math.round(currentOrder.totalPrice) / 100,
        Math.round(currentOrder.penaltyFee) / 100,
        currentOrder?.coupon?.code,
        orderCreated?.friendlyId,
        {
          email: user?.email || '',
          phone_number: user?.mobile?.phone || '',
        }
      );

      if (orderCreated.paymentUrl) {
        window.location = orderCreated.paymentUrl;
        console.debug('OrderDeliveryForm', 'window.location', window.location);
        return;
      }

      const orderUpdated = {
        ...order,
        serverResponse: orderCreated,
      };
      Order.update(orderUpdated);

      Cart.reset();
      CouponDiscount.update(0);
      setIsModalSuccessOpen(true);
    } catch (error) {
      console.error(error); // TODO: show in error modal
    } finally {
      Loader.removeEvent('SUBMIT_ORDER');
    }
  }

  function modifyProduct() {
    const allProducts = productsForOrder.getStorageValue();
    const products = allProducts?.filter(p => !p.isSpecialOffer);
    const offers = allProducts?.filter(p => p.isSpecialOffer);

    const offersFiltered = offers?.filter((value, index, self) => self?.map(p => p._id)?.indexOf(value._id) === index);
    const productsFiltered = products?.filter(
      (value, index, self) => self?.map(p => p._id)?.indexOf(value._id) === index
    );

    const modifiedProducts = productsFiltered?.map(p => {
      const finalPriceText = p.finalPrice;
      const quantity = productsQuantities.getValue()[p._id];
      return {
        ...p,
        name: p.displayName,
        price: finalPriceText,
        quantity,
        comments: p.userComments,
      };
    });

    const modifiedOffers = offersFiltered?.map(o => {
      if (o.products.length) {
        const finalPriceText = o.finalPrice;
        const quantity = productsQuantities.getValue()[o._id];
        return {
          _id: o._id,
          name: o.name,
          price: finalPriceText,
          quantity,
        };
      }

      if (o.groups.length) {
        const finalPriceText = o.finalPrice;
        const quantity = productsQuantities.getValue()[o._id];
        const groups = o.groups.map(g => ({
          _id: g._id,
          products: [
            {
              _id: g.products[0].productId,
              sku: g.products[0].sku,
            },
          ],
        }));

        return {
          _id: o._id,
          name: o.name,
          price: finalPriceText,
          quantity,
          groups,
        };
      }
      return null;
    });

    return [modifiedProducts, modifiedOffers];
  }

  async function checkOrderOnCheckout(_currentOrder) {
    const _guestUser = GuestUserStore.getValue();
    const currentOrder = {
      ..._currentOrder,
      user: {
        phone: _guestUser?.contactPhone,
        email: _guestUser?.email,
        isGuestUser: Boolean(_guestUser?.email),
      },
    };
    orderCheckResponse = await checkOrder(currentOrder);

    if (orderCheckResponse.error) {
      const { code: errorCode } = orderCheckResponse?.error?.response?.data?.error || {};
      const { userMessage } = orderCheckResponse?.error?.response?.data?.error || {};
      if (errorCode === 'ORDVD032') {
        const disabledDiscountCampaignIds =
          orderCheckResponse?.error?.response?.data?.error?.params.disabledDiscountCampaignIds;
        setErrorModalMessage(userMessage);
        setModalType('genericErrorInfo');
        const { campaigns, error } = await getCampaigns(true);
        if (error) {
          handleCreateOrderError();
          return;
        }
        const service = DeliveryMode.getValue();
        const collectionType = DeliveryMode.getCollectionAsNumber(service);
        await ProductsCollection.updateCollection(collectionType);
        const discountCampaignsApplied = currentOrder?.discountCampaignsApplied.filter(
          campaignID => !disabledDiscountCampaignIds.includes(campaignID)
        );
        Order.setDiscountCampaignsApplied(discountCampaignsApplied);
        CampaignStore.update(campaigns);
      } else if (errorCode === 'OCHINT002' || errorCode === 'OCHINT013') {
        setIsProductChanges(true);
        const productIds = orderCheckResponse?.error?.response?.data?.error?.params || [];
        const allProducts = productsForOrder.getStorageValue();
        const _unavailableProducts = productIds.map(id => allProducts.find(product => product._id === id));
        setUnavailableProducts(_unavailableProducts);
      } else if (errorCode === 'ORDVD021') {
        setErrorModalMessage(userMessage);
        setModalType('expressLimitReached');
      } else {
        setErrorModalMessage(userMessage);
        setModalType('genericErrorInfo');
      }
    }
    setBagsInfo(orderCheckResponse.plasticBagsInfo);
    updateTotalPrice(orderCheckResponse?.plasticBagsInfo?.price || 0);
  }

  function updateTotalPrice(bagsFee) {
    const price = totalCartPriceStore.getValue();
    const deliveryModePrice = DeliveryMode.getValue();
    setFinalCartPrice(Math.round(price));
    if (deliveryModePrice === 'express') {
      const finalPrice = price + EXPRESS_FEE + bagsFee;
      setFee(EXPRESS_FEE);
      Order.setTotalPrice(Math.round(finalPrice));
    }
    if (deliveryModePrice === 'easy') {
      if (price < MIN_CART_PRICE) {
        const finalPrice = price + PENALTY_FEE + bagsFee;
        setFee(PENALTY_FEE);
        Order.setTotalPrice(Math.round(finalPrice));
      } else {
        const finalPrice = price + bagsFee;
        Order.setTotalPrice(Math.round(finalPrice));
      }
    }
    if (deliveryModePrice === 'takeAway') {
      const finalPrice = price + bagsFee;
      Order.setTotalPrice(Math.round(finalPrice));
    }
  }

  function getShopId(serviceSelected) {
    const shop = Shop.getStorageValue();
    if (serviceSelected === 'easy') {
      Order.setShopId(shop?.easyShop?._id);
    } else if (serviceSelected === 'express') {
      Order.setShopId(shop?.expressShop?._id);
    } else {
      Order.setShopId(shop?.takeAwayShop?._id);
    }
  }

  function onSubmit(data) {
    const hasTimeslotError = onClickSubmit();
    if (!hasTimeslotError) {
      if (data.companyAddress) {
        const invoiceData = {
          companyAddress: data.companyAddress,
          companyName: data.companyName,
          companyPhone: data.companyPhone,
          profession: data.profession,
          AFM: data.AFM,
          DOY: data.DOY,
        };
        Order.setInvoiceInfo(invoiceData);
      } else {
        Order.deleteProperty(['invoice']);
      }
      onSubmitOrder();
    }
  }

  useEffect(() => {
    Cart.setIsEditable(false);
    Cart.setIsVisible(false);
    const deliveryMode$ = DeliveryMode.subscribe(service => {
      setDeliveryMode(service);
      getShopId(service);
      const orderShippingType = service === 'takeAway' ? 'takeAway' : 'delivery';
      const order = Order.getValue();
      Order.update({
        ...order,
        selectedService: service,
        orderShippingType,
        redeemPoints: false,
        couponCode: undefined,
      });
    });

    const user$ = combineLatest([User._lazyInit(), GuestUserStore._lazyInit()]).subscribe(res => {
      const user = res[0];
      const guest = res[1];
      if (!user?._id && !guest?.email) {
        router.replace('/');
      }
    });

    const guestUser$ = GuestUserStore.subscribe(_guestUser => {
      setGuestUser(_guestUser);
    });

    const price$ = cartPrice.subject
      .pipe(
        filter(v => v),
        distinctUntilChanged()
      )
      .subscribe(price => {
        const currentService = DeliveryMode.getValue();
        if (currentService === 'easy' && price < MIN_CART_PRICE) {
          Order.setPenalty(PENALTY_FEE, MIN_CART_PRICE);
          return;
        }
        if (currentService === 'express') {
          Order.setPenalty(EXPRESS_FEE, undefined);
          return;
        }
        if (currentService === 'takeAway') {
          Order.setPenalty(0, MIN_CART_TAKEAWAY);
          return;
        }
        Order.setPenalty(undefined, undefined);
      });

    const [orderProducts = [], orderOffers = []] = modifyProduct();

    const products = orderProducts.length ? orderProducts : undefined;
    Order.setProducts(products);

    const offers = orderOffers.length ? orderOffers : undefined;
    Order.setOffers(offers);

    const shops$ = TakeAwayShops.subscribe(setTakeAwayShops);
    const order$ = Order.subject
      .pipe(distinctUntilChanged((prev, curr) => prev?.wantPlasticBags === curr?.wantPlasticBags))
      .subscribe(_order => {
        if (!orderCheckResponse || _order === undefined) return;
        if (!_order?.wantPlasticBags) {
          updateTotalPrice(0);
          setBagsInfo({ amount: 0, price: 0 });
          return;
        }
        updateTotalPrice(orderCheckResponse?.plasticBagsInfo?.price || 0);
        setBagsInfo(orderCheckResponse.plasticBagsInfo);
      });
    const order = Order.getValue();
    const _orderWithBags = { ...order, totalPrice: undefined };
    checkOrderOnCheckout(_orderWithBags);

    async function checkForOfferChange() {
      const productsInCart = productsForOrder.getStorageValue();
      const offersWithPriceChange = (await getOffersWithPriceChange(productsInCart)) || [];
      if (offersWithPriceChange.length) {
        setPriceChangeProducts(offersWithPriceChange);
        setIsProductChanges(true);
      }
    }
    checkForOfferChange();
    const isEligibleToRate$ = User.isEligibleToRate$().subscribe(setIsEligibleToRate);
    return () => {
      console.debug('OrderDeliveryForm', 'useEffect', 'unmount');
      user$.unsubscribe();
      deliveryMode$.unsubscribe();
      isEligibleToRate$.unsubscribe();
      shops$.unsubscribe();
      order$.unsubscribe();
      price$.unsubscribe();
      guestUser$.unsubscribe();
    };
  }, []);

  const unsupportedPostalCodeError = (() => {
    if (!postalCodeUnsupported) return '';

    if (deliveryMode === 'takeAway') return TAKEAWAY_UNSUPPORTED_POSTAL_CODE_ERROR;

    return DELIVERY_UNSUPPORTED_POSTAL_CODE_ERROR;
  })();

  function getInfoModalFunction() {
    switch (modalType) {
      case 'expressLimitReached':
        return () => {
          setModalType('');
          router.back();
        };
      case 'noTimeslot':
        return () => {
          setModalType('');
          window.scrollTo({ top: 500, behavior: 'smooth' });
        };
      default:
        return () => setModalType('');
    }
  }

  useEffect(() => {
    const errorsArray = Object.keys(errors);
    if (errorsArray[0]) {
      const inputWithError = document.querySelector(`[name="${errorsArray[0]}"]`);
      inputWithError.scrollIntoView({
        behavior: 'auto',
        block: 'center',
      });
    }
  }, [errors]);

  return (
    <>
      <div className={style.checkoutForm}>
        {deliveryMode === 'takeAway' && <ShopPicker setOpenTakeAwayModal={setOpenTakeAwayModal} />}
        {(deliveryMode !== 'takeAway' || guestUser?.email) && (
          <AddressPicker
            className={style.addressPickerContainer}
            guestTakeAway={deliveryMode === 'takeAway' && guestUser?.email}
          />
        )}
        <TimeSlotsToDeliver setPostalCodeUnsupported={setPostalCodeUnsupported} />
        {unsupportedPostalCodeError ? (
          <div className={style.errorMsg}>
            <p>{unsupportedPostalCodeError}</p>
          </div>
        ) : (
          <form noValidate className={style.form} name="orderDeliveryForm" onSubmit={handleSubmit(onSubmit)}>
            <OrderPreferences control={control} bagsInfo={bagsInfo} couponNo={couponNo} />
            <div className={style.btnContainer}>
              <PrimaryButton type="submit" data-cy="submit-order-CTA">
                Αποστολή Παραγγελίας
              </PrimaryButton>
            </div>
          </form>
        )}
      </div>

      {deliveryMode === 'takeAway' && (
        <TakeAwayModal
          isOpen={isTakeAwayModalOpen}
          setIsOpen={setOpenTakeAwayModal}
          shops={takeAwayShops}
          onShopClicked={onClickSelectStore}
        />
      )}
      {isModalSuccessOpen && (
        <OrderAlertSuccess
          isModalOpen={isModalSuccessOpen}
          onClickOk={onClickOkReceipt}
          isForGuestUser={!!guestUser?.email}
        />
      )}
      {isConfirmationModalOpen && (
        <ConfirmationModal
          mainText={errorModalMessage}
          isOpen={isConfirmationModalOpen}
          modalLabel="Confirmation modal"
          backBtnText="Ακυρο"
          continueBtnText="Συνέχεια"
          onClickContinue={onAcceptCharges}
          onClickBack={() => setIsConfirmationModalOpen(false)}
          modalClose={() => setIsConfirmationModalOpen(false)}
        />
      )}
      {modalType && (
        <InfoModal
          mainText={errorModalMessage}
          onClickContinue={() => getInfoModalFunction()()}
          continueBtnText="Συνέχεια"
          modalLabel="Info modal"
        />
      )}
      {isProductChanges && (
        <ProductsChangesModal
          isModalOpen={isProductChanges}
          setIsModalOpen={setIsProductChanges}
          updatedProducts={priceChangeProducts}
          unavailableProducts={unavailableProducts}
          modifyProduct={modifyProduct}
        />
      )}
      <AppRatingModal
        isModalOpen={isAppRatingModalOpen}
        onSuccessfulSubmit={onSuccessfulRatingSubmit}
        onClose={() => {
          setIsAppRatingModalOpen(false);
          router.replace('/');
        }}
      />
    </>
  );
}
